import OpenReplay from '@openreplay/tracker';
import { PublicUserConfig } from 'src/types/widget';
import { areCookiesEnabled } from 'src/utils/analytics';
import { config } from 'src/utils/config';
import trackerAssist from '@openreplay/tracker-assist';
import * as Sentry from '@sentry/vue';

let tracker: OpenReplay | null = null;
let isInitialized = false;

function captureUtmParams() {
  if (!tracker) return;

  const searchParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  for (const [key, value] of searchParams.entries()) {
    if (key.toLowerCase().startsWith('utm_')) {
      utmParams[key] = value;
    }
  }

  if (Object.keys(utmParams).length > 0) {
    tracker.setMetadata('utm_params', JSON.stringify(utmParams));
  }
}

export async function init() {
  if (isInitialized || typeof window === 'undefined') {
    return;
  }
  isInitialized = true;

  const projectKey = config.value.analytics?.openReplayProjectKey || env.APP_OPENREPLAY_PROJECT_KEY;

  if (areCookiesEnabled() && projectKey) {
    tracker = new OpenReplay({
      projectKey,
      respectDoNotTrack: true,
      autoResetOnWindowOpen: true,
      __DISABLE_SECURE_MODE: env.APP_ENV === 'local',
    });
    tracker.use(trackerAssist({}));
    await tracker.start();
    const sessionId = tracker.getSessionID();
    const sessionURL = tracker.getSessionURL();
    if (sessionId) {
      Sentry.setTag('openReplaySession.id', sessionId);
    }
    if (sessionURL) {
      Sentry.setTag('openReplaySessionURL', sessionURL);
    }
    isInitialized = true;

    captureUtmParams();

    if (config.value.driver) {
      setUserData(config.value.driver);
    }
  }
}

export function setUserData(user: PublicUserConfig) {
  if (!tracker) return;

  if (user.id) {
    tracker.setUserID(user.id);
  }
  if (user.email) {
    tracker.setMetadata('user_email', user.email);
  }
  tracker.setMetadata('user_name', `${user.firstName || ''} ${user.lastName || ''}`.trim());
}

export function trackEvent(eventName: string, payload?: unknown) {
  if (!tracker) return;
  tracker.event(eventName, payload);
}

export function trackIssue(eventName: string, payload?: unknown) {
  if (!tracker) return;
  tracker.issue(eventName, payload);
}

export function setMetadata(key: string, value: string) {
  if (!tracker) return;
  tracker.setMetadata(key, value);
}

export function stop() {
  if (tracker) {
    tracker.stop();
    tracker = null;
    isInitialized = false;
  }
}

export function isActive() {
  return tracker?.isActive() || false;
}

export function getSessionId() {
  return tracker?.getSessionID() || null;
}
export function getSessionURL() {
  return tracker?.getSessionURL() || null;
}
